import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, Card } from '@/components/ui';
import { formatDate } from '@/lib/utils/date';
import { routePaths } from '@/app/routes';
import type { NomenclatureResolver } from '@/lib/nomenclature/resolver';
import { showsInProgress } from '../lib/categories';
import type { MedicalResult } from '../types/result.types';

interface ResultCardProps {
  result: MedicalResult;
  resolver: NomenclatureResolver | null;
}

/** Compose "Dr. Nume Prenume" from the physician's degree + name when available. */
function physicianLabel(resolver: NomenclatureResolver | null, result: MedicalResult): string | null {
  const name = resolver?.physicianName(result.physicianId);
  if (!name) return null;
  const degree = resolver?.physicianDegree(result.physicianId);
  return degree ? `${degree} ${name}` : name;
}

/**
 * One result/visit card — same information the mobile `VisitCard` shows:
 * location, visit title, physician, date and a document summary. Tapping it
 * opens the detail page.
 */
export function ResultCard({ result, resolver }: ResultCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = resolver?.locationName(result.locationId);
  const physician = physicianLabel(resolver, result);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => navigate(`${routePaths.results}/${encodeURIComponent(result.consId)}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`${routePaths.results}/${encodeURIComponent(result.consId)}`);
        }
      }}
      className="flex cursor-pointer items-stretch gap-4 p-4 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <div className="min-w-0 flex-1">
        {location && <p className="truncate text-xs font-semibold uppercase tracking-wide text-primary">{location}</p>}
        <p className="mt-0.5 truncate font-semibold text-ink">
          {result.consDesc || result.typeDesc || t('results.untitled')}
        </p>
        {physician && <p className="mt-0.5 truncate text-sm text-secondary">{physician}</p>}
        <p className="mt-1 text-sm text-ink-mute">{formatDate(result.date)}</p>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {(result.isFinal || showsInProgress(result.consType)) && (
            <Badge tone={result.isFinal ? 'success' : 'warning'}>
              {result.isFinal ? t('results.final') : t('results.in_progress')}
            </Badge>
          )}
          {result.docCount > 0 && (
            <span className="text-xs text-ink-mute">
              {t('results.documents_summary', { available: result.docCountAvail, total: result.docCount })}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center text-ink-mute">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </Card>
  );
}
